/**
 * BuildYourOwnTab - Admin management for Build Your Own items
 * Full CRUD: Create, Read, Update, Delete items with sections and options
 * Includes analytics, popular builds tracking, and Chef's Recommendations
 */
import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Plus, Edit2, Trash2, Eye, EyeOff, TrendingUp, 
  BarChart2, Award, Clock, ChefHat, Sandwich, Egg,
  RefreshCw, Star, Sparkles, Leaf, Zap, Save, X,
  GripVertical, Copy, Image, ChevronDown, ChevronUp,
  Settings, Package, Upload
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { api } from '@/utils/apiClient';

// Empty templates for new items
const EMPTY_OPTION = {
  name: '',
  price: 0,
  category: '',
  is_default: false,
  max_quantity: 3
};

const EMPTY_SECTION = {
  name: '',
  category: '',
  min_selections: 0,
  max_selections: 1,
  options: [{ ...EMPTY_OPTION }]
};

const EMPTY_ITEM = {
  id: '',
  name: '',
  description: '',
  base_price: 0,
  category: 'build-your-own',
  image: '',
  sections: [],
  is_available: true,
  prep_time_minutes: 10
};

export default function BuildYourOwnTab() {
  const [activeTab, setActiveTab] = useState('items');
  const [items, setItems] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [popularOptions, setPopularOptions] = useState([]);
  const [chefRecommendations, setChefRecommendations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingItem, setEditingItem] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showChefModal, setShowChefModal] = useState(false);
  const [editingChefRec, setEditingChefRec] = useState(null);
  const [newItem, setNewItem] = useState({ ...EMPTY_ITEM });
  const [expandedSections, setExpandedSections] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  const [newChefRec, setNewChefRec] = useState({
    builder_id: '',
    name: '',
    description: '',
    badge_type: 'chef_pick',
    special_price: ''
  });

  // Handle image upload for BYO items
  const handleImageUpload = async (e, isEditing = false) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast.error('Please upload an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Image must be less than 5MB');
      return;
    }

    setUploadingImage(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const res = await api.post('/admin/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const imageUrl = res.data.url.startsWith('/') ? res.data.url : `/${res.data.url}`;
      
      if (isEditing) {
        setEditingItem(prev => ({ ...prev, image: imageUrl }));
      } else {
        setNewItem(prev => ({ ...prev, image: imageUrl }));
      }
      toast.success('Image uploaded successfully');
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('Failed to upload image');
    }
    setUploadingImage(false);
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Fetch items, analytics, chef recommendations in parallel
      const [itemsRes, analyticsRes, popularRes, chefRes] = await Promise.all([
        api.get('/admin/build-your-own'),
        api.get('/admin/build-analytics'),
        api.get('/admin/build-analytics/popular-options'),
        api.get('/admin/chef-recommendations')
      ]);

      setItems(itemsRes.data);
      setAnalytics(analyticsRes.data);
      setPopularOptions(popularRes.data.popular_options || []);
      setChefRecommendations(chefRes.data);
    } catch (err) {
      console.error('Failed to fetch data:', err);
      toast.error('Failed to load data');
    }
    setLoading(false);
  };

  // Chef's Recommendations functions
  const createChefRecommendation = async () => {
    if (!newChefRec.builder_id || !newChefRec.name) {
      toast.error('Please select a builder item and enter a name');
      return;
    }

    // Get default selections from the builder
    const builder = items.find(i => i.id === newChefRec.builder_id);
    if (!builder) {
      toast.error('Builder item not found');
      return;
    }

    const defaultSelections = {};
    builder.sections?.forEach(section => {
      const defaults = section.options
        .filter(opt => opt.is_default)
        .map(opt => ({ name: opt.name, price: opt.price, quantity: 1 }));
      if (defaults.length > 0) {
        defaultSelections[section.category] = defaults;
      }
    });

    try {
      await api.post('/admin/chef-recommendations', {
        builder_id: newChefRec.builder_id,
        name: newChefRec.name,
        description: newChefRec.description,
        badge_type: newChefRec.badge_type,
        special_price: newChefRec.special_price ? parseFloat(newChefRec.special_price) : null,
        selections: defaultSelections,
        is_active: true
      });

      toast.success('Chef\'s recommendation created!');
      setShowChefModal(false);
      setNewChefRec({ builder_id: '', name: '', description: '', badge_type: 'chef_pick', special_price: '' });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create recommendation');
    }
  };

  const promoteToChefPick = async (popularBuild) => {
    const name = window.prompt('Enter a name for this Chef\'s Pick:', `${popularBuild.builder_name} Special`);
    if (!name) return;

    try {
      await api.post(`/admin/chef-recommendations/from-popular/${popularBuild.id}`, null, {
        params: { name, badge_type: 'chef_pick' }
      });

      toast.success('Promoted to Chef\'s Pick!');
      fetchData();
    } catch (err) {
      toast.error('Failed to promote');
    }
  };

  const toggleChefRec = async (recId) => {
    try {
      const res = await api.patch(`/admin/chef-recommendations/${recId}/toggle`);

      setChefRecommendations(prev => prev.map(rec =>
        rec.id === recId ? { ...rec, is_active: res.data.is_active } : rec
      ));
      toast.success(`Recommendation ${res.data.is_active ? 'activated' : 'deactivated'}`);
    } catch (err) {
      toast.error('Failed to toggle recommendation');
    }
  };

  const deleteChefRec = async (recId) => {
    if (!window.confirm('Delete this Chef\'s recommendation?')) return;

    try {
      await api.delete(`/admin/chef-recommendations/${recId}`);

      setChefRecommendations(prev => prev.filter(rec => rec.id !== recId));
      toast.success('Recommendation deleted');
    } catch (err) {
      toast.error('Failed to delete recommendation');
    }
  };

  const getBadgeIcon = (badgeType) => {
    if (badgeType === 'seasonal') return Leaf;
    if (badgeType === 'new') return Zap;
    if (badgeType === 'staff_favorite') return Sparkles;
    return Star;
  };

  const getBadgeColor = (badgeType) => {
    if (badgeType === 'seasonal') return 'bg-green-500';
    if (badgeType === 'new') return 'bg-purple-500';
    if (badgeType === 'staff_favorite') return 'bg-blue-500';
    return 'bg-amber-500';
  };

  const toggleAvailability = async (itemId) => {
    try {
      const res = await api.patch(`/admin/build-your-own/${itemId}/toggle`);
      
      setItems(prev => prev.map(item => 
        item.id === itemId ? { ...item, is_available: res.data.is_available } : item
      ));
      toast.success(`Item ${res.data.is_available ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error('Failed to toggle availability');
    }
  };

  const deleteItem = async (itemId) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      await api.delete(`/admin/build-your-own/${itemId}`);
      
      setItems(prev => prev.filter(item => item.id !== itemId));
      toast.success('Item deleted');
    } catch (err) {
      toast.error('Failed to delete item');
    }
  };

  const getItemIcon = (itemId) => {
    if (itemId.includes('burger')) return Sandwich;
    if (itemId.includes('breakfast') || itemId.includes('eggs') || itemId.includes('omelette')) return Egg;
    return ChefHat;
  };

  // ========== SECTION & OPTION MANAGEMENT ==========
  
  // Toggle section expansion in edit modal
  const toggleSectionExpand = (sectionIndex) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionIndex]: !prev[sectionIndex]
    }));
  };

  // Add new section to item being edited/created
  const addSection = (isNew = false) => {
    const newSection = {
      ...EMPTY_SECTION,
      category: `section-${Date.now()}`,
      options: [{ ...EMPTY_OPTION, category: `section-${Date.now()}` }]
    };
    
    if (isNew) {
      setNewItem(prev => ({
        ...prev,
        sections: [...(prev.sections || []), newSection]
      }));
    } else {
      setEditingItem(prev => ({
        ...prev,
        sections: [...(prev.sections || []), newSection]
      }));
    }
  };

  // Remove section from item
  const removeSection = (sectionIndex, isNew = false) => {
    if (isNew) {
      setNewItem(prev => ({
        ...prev,
        sections: prev.sections.filter((_, i) => i !== sectionIndex)
      }));
    } else {
      setEditingItem(prev => ({
        ...prev,
        sections: prev.sections.filter((_, i) => i !== sectionIndex)
      }));
    }
  };

  // Update section field
  const updateSection = (sectionIndex, field, value, isNew = false) => {
    const updateFn = (prev) => ({
      ...prev,
      sections: prev.sections.map((section, i) => {
        if (i !== sectionIndex) return section;
        const updated = { ...section, [field]: value };
        // If category changes, update all options' category too
        if (field === 'category') {
          updated.options = section.options.map(opt => ({ ...opt, category: value }));
        }
        return updated;
      })
    });
    
    if (isNew) {
      setNewItem(updateFn);
    } else {
      setEditingItem(updateFn);
    }
  };

  // Add option to section
  const addOption = (sectionIndex, isNew = false) => {
    const updateFn = (prev) => ({
      ...prev,
      sections: prev.sections.map((section, i) => {
        if (i !== sectionIndex) return section;
        return {
          ...section,
          options: [...section.options, { ...EMPTY_OPTION, category: section.category }]
        };
      })
    });
    
    if (isNew) {
      setNewItem(updateFn);
    } else {
      setEditingItem(updateFn);
    }
  };

  // Remove option from section
  const removeOption = (sectionIndex, optionIndex, isNew = false) => {
    const updateFn = (prev) => ({
      ...prev,
      sections: prev.sections.map((section, i) => {
        if (i !== sectionIndex) return section;
        return {
          ...section,
          options: section.options.filter((_, j) => j !== optionIndex)
        };
      })
    });
    
    if (isNew) {
      setNewItem(updateFn);
    } else {
      setEditingItem(updateFn);
    }
  };

  // Update option field
  const updateOption = (sectionIndex, optionIndex, field, value, isNew = false) => {
    const updateFn = (prev) => ({
      ...prev,
      sections: prev.sections.map((section, i) => {
        if (i !== sectionIndex) return section;
        return {
          ...section,
          options: section.options.map((option, j) => {
            if (j !== optionIndex) return option;
            return { ...option, [field]: value };
          })
        };
      })
    });
    
    if (isNew) {
      setNewItem(updateFn);
    } else {
      setEditingItem(updateFn);
    }
  };

  // Create new BYO item
  const createItem = async () => {
    if (!newItem.name || !newItem.base_price) {
      toast.error('Please enter a name and base price');
      return;
    }
    
    if (newItem.sections.length === 0) {
      toast.error('Please add at least one section');
      return;
    }
    
    setSaving(true);
    try {
      const itemToCreate = {
        ...newItem,
        id: newItem.id || `byo-${newItem.name.toLowerCase().replace(/\s+/g, '-')}-${Date.now()}`,
        base_price: parseFloat(newItem.base_price) || 0,
        prep_time_minutes: parseInt(newItem.prep_time_minutes) || 10
      };
      
      await api.post('/admin/build-your-own', itemToCreate);
      
      toast.success('Build Your Own item created!');
      setShowCreateModal(false);
      setNewItem({ ...EMPTY_ITEM });
      setExpandedSections({});
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create item');
    }
    setSaving(false);
  };

  // Save edited item
  const saveEditedItem = async () => {
    if (!editingItem.name || !editingItem.base_price) {
      toast.error('Please enter a name and base price');
      return;
    }
    
    setSaving(true);
    try {
      const itemToSave = {
        ...editingItem,
        base_price: parseFloat(editingItem.base_price) || 0,
        prep_time_minutes: parseInt(editingItem.prep_time_minutes) || 10
      };
      
      await api.put(`/admin/build-your-own/${editingItem.id}`, itemToSave);
      
      toast.success('Item updated successfully');
      setShowEditModal(false);
      setExpandedSections({});
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update item');
    }
    setSaving(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl">Build Your Own</h2>
          <p className="text-muted-foreground text-sm">Manage customizable menu items and view analytics</p>
        </div>
        <Button onClick={fetchData} variant="outline" className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="items" className="gap-2">
            <ChefHat className="w-4 h-4" />
            Items ({items.length})
          </TabsTrigger>
          <TabsTrigger value="chef" className="gap-2">
            <Star className="w-4 h-4" />
            Chef's Picks ({chefRecommendations.length})
          </TabsTrigger>
          <TabsTrigger value="analytics" className="gap-2">
            <BarChart2 className="w-4 h-4" />
            Analytics
          </TabsTrigger>
          <TabsTrigger value="popular" className="gap-2">
            <Award className="w-4 h-4" />
            Popular Builds
          </TabsTrigger>
        </TabsList>

        {/* Chef's Recommendations Tab */}
        <TabsContent value="chef" className="mt-6">
          <div className="space-y-6">
            {/* Create New Button */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-muted-foreground">
                Highlight your best build combinations as Chef's Picks
              </p>
              <Button onClick={() => setShowChefModal(true)} className="gap-2 bg-amber-500 hover:bg-amber-600">
                <Plus className="w-4 h-4" />
                New Chef's Pick
              </Button>
            </div>

            {/* Recommendations Grid */}
            {chefRecommendations.length > 0 ? (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                {chefRecommendations.map(rec => {
                  const BadgeIcon = getBadgeIcon(rec.badge_type);
                  const badgeColor = getBadgeColor(rec.badge_type);
                  return (
                    <motion.div
                      key={rec.id}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      className={`bg-card rounded-xl border overflow-hidden ${!rec.is_active ? 'opacity-60' : ''}`}
                    >
                      <div className="relative h-28">
                        <img
                          src={rec.builder_image || 'https://images.pexels.com/photos/1639557/pexels-photo-1639557.jpeg'}
                          alt={rec.name}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute top-2 left-2">
                          <span className={`px-2 py-1 ${badgeColor} text-white text-xs rounded-full flex items-center gap-1`}>
                            <BadgeIcon className="w-3 h-3" />
                            {rec.badge_type === 'chef_pick' ? "Chef's Pick" : rec.badge_type === 'seasonal' ? 'Seasonal' : rec.badge_type === 'new' ? 'New' : 'Staff Favorite'}
                          </span>
                        </div>
                        {!rec.is_active && (
                          <div className="absolute top-2 right-2">
                            <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">Inactive</span>
                          </div>
                        )}
                      </div>
                      
                      <div className="p-4">
                        <h3 className="font-medium text-sm mb-1">{rec.name}</h3>
                        <p className="text-xs text-muted-foreground mb-2">{rec.builder_name}</p>
                        
                        <div className="flex items-center gap-2 mb-3">
                          {rec.special_price ? (
                            <>
                              <span className="font-bold text-accent">${rec.special_price.toFixed(2)}</span>
                              <span className="text-xs text-muted-foreground line-through">${rec.calculated_price?.toFixed(2)}</span>
                            </>
                          ) : (
                            <span className="font-bold text-accent">${rec.calculated_price?.toFixed(2)}</span>
                          )}
                          {rec.order_count > 0 && (
                            <span className="text-xs text-muted-foreground">• {rec.order_count} orders</span>
                          )}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => toggleChefRec(rec.id)}
                            className="flex-1"
                          >
                            {rec.is_active ? (
                              <><EyeOff className="w-3 h-3 mr-1" /> Hide</>
                            ) : (
                              <><Eye className="w-3 h-3 mr-1" /> Show</>
                            )}
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive"
                            onClick={() => deleteChefRec(rec.id)}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            ) : (
              <div className="text-center py-12 bg-muted/30 rounded-xl">
                <Star className="w-12 h-12 text-amber-400 mx-auto mb-4" />
                <h3 className="font-medium mb-2">No Chef's Recommendations Yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create your first Chef's Pick to highlight special combinations
                </p>
                <Button onClick={() => setShowChefModal(true)} className="bg-amber-500 hover:bg-amber-600">
                  <Plus className="w-4 h-4 mr-2" />
                  Create Chef's Pick
                </Button>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Items Tab */}
        <TabsContent value="items" className="mt-6">
          {/* Create New Button */}
          <div className="flex justify-between items-center mb-6">
            <p className="text-sm text-muted-foreground">
              Manage customizable menu items with sections and options
            </p>
            <Button 
              onClick={() => {
                setNewItem({ ...EMPTY_ITEM });
                setExpandedSections({});
                setShowCreateModal(true);
              }} 
              className="gap-2 bg-accent hover:bg-accent/90"
              data-testid="create-byo-item-btn"
            >
              <Plus className="w-4 h-4" />
              Create New Item
            </Button>
          </div>
          
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {[...Array(6)].map((_, i) => (
                <div key={i} className="h-48 bg-muted rounded-xl animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {items.map(item => {
                const Icon = getItemIcon(item.id);
                return (
                  <motion.div
                    key={item.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className={`bg-card rounded-xl border overflow-hidden ${
                      !item.is_available ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="relative h-32">
                      <img 
                        src={item.image} 
                        alt={item.name}
                        className="w-full h-full object-cover"
                      />
                      <div className="absolute top-2 right-2 flex gap-1">
                        {!item.is_available && (
                          <span className="px-2 py-1 bg-red-500 text-white text-xs rounded-full">
                            Disabled
                          </span>
                        )}
                      </div>
                      <div className="absolute bottom-2 left-2">
                        <span className="px-2 py-1 bg-card/95 dark:bg-gray-900/95 text-foreground text-sm font-medium rounded-full border border-border shadow-sm">
                          ${item.base_price.toFixed(2)}
                        </span>
                      </div>
                    </div>
                    
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon className="w-4 h-4 text-accent" />
                          <h3 className="font-medium text-sm">{item.name}</h3>
                        </div>
                      </div>
                      
                      <p className="text-xs text-muted-foreground mb-3 line-clamp-2">
                        {item.sections?.length || 0} sections • {item.prep_time_minutes} min prep
                      </p>
                      
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => toggleAvailability(item.id)}
                          className="flex-1"
                        >
                          {item.is_available ? (
                            <><EyeOff className="w-3 h-3 mr-1" /> Disable</>
                          ) : (
                            <><Eye className="w-3 h-3 mr-1" /> Enable</>
                          )}
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setEditingItem(item);
                            setShowEditModal(true);
                          }}
                        >
                          <Edit2 className="w-3 h-3" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-destructive"
                          onClick={() => deleteItem(item.id)}
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}
        </TabsContent>

        {/* Analytics Tab */}
        <TabsContent value="analytics" className="mt-6">
          {analytics ? (
            <div className="space-y-6">
              {/* Summary Cards */}
              <div className="grid md:grid-cols-3 gap-4">
                <div className="bg-card rounded-xl p-6 border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-accent" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{analytics.summary?.total_orders || 0}</p>
                      <p className="text-xs text-muted-foreground">Total BYO Orders</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-card rounded-xl p-6 border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                      <ChefHat className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{analytics.summary?.total_combinations || 0}</p>
                      <p className="text-xs text-muted-foreground">Unique Combinations</p>
                    </div>
                  </div>
                </div>
                
                <div className="bg-card rounded-xl p-6 border">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="w-10 h-10 rounded-full bg-green-500/10 flex items-center justify-center">
                      <Award className="w-5 h-5 text-green-500" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold">{items.filter(i => i.is_available).length}</p>
                      <p className="text-xs text-muted-foreground">Active Items</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* By Builder Stats */}
              <div className="bg-card rounded-xl p-6 border">
                <h3 className="font-medium mb-4">Orders by Item</h3>
                <div className="space-y-3">
                  {analytics.by_builder?.length > 0 ? (
                    analytics.by_builder.map(stat => {
                      const item = items.find(i => i.id === stat._id);
                      const Icon = getItemIcon(stat._id);
                      return (
                        <div key={stat._id} className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                          <div className="flex items-center gap-3">
                            <Icon className="w-5 h-5 text-muted-foreground" />
                            <div>
                              <p className="font-medium text-sm">{item?.name || stat._id}</p>
                              <p className="text-xs text-muted-foreground">
                                {stat.unique_combinations} combinations
                              </p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-bold text-accent">{stat.total_orders}</p>
                            <p className="text-xs text-muted-foreground">orders</p>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <p className="text-muted-foreground text-sm text-center py-4">
                      No order data yet. Orders will appear here once customers start building!
                    </p>
                  )}
                </div>
              </div>

              {/* Popular Options */}
              <div className="bg-card rounded-xl p-6 border">
                <h3 className="font-medium mb-4">Most Popular Options</h3>
                <div className="flex flex-wrap gap-2">
                  {popularOptions.length > 0 ? (
                    popularOptions.slice(0, 15).map((opt, i) => (
                      <span
                        key={i}
                        className={`px-3 py-1.5 rounded-full text-sm ${
                          i === 0 ? 'bg-accent text-white' :
                          i < 3 ? 'bg-accent/20 text-accent' :
                          'bg-muted text-muted-foreground'
                        }`}
                      >
                        {opt.name} ({opt.count})
                      </span>
                    ))
                  ) : (
                    <p className="text-muted-foreground text-sm">
                      Popular options will appear here as orders come in.
                    </p>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center py-12">
              <BarChart2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">Loading analytics...</p>
            </div>
          )}
        </TabsContent>

        {/* Popular Builds Tab */}
        <TabsContent value="popular" className="mt-6">
          <div className="space-y-6">
            <div className="bg-card rounded-xl p-6 border">
              <h3 className="font-medium mb-4 flex items-center gap-2">
                <Award className="w-5 h-5 text-accent" />
                Top Build Combinations
              </h3>
              
              {analytics?.most_popular?.length > 0 ? (
                <div className="space-y-4">
                  {analytics.most_popular.map((build, i) => {
                    const Icon = getItemIcon(build.builder_id);
                    return (
                      <div key={build.id || i} className="p-4 bg-muted/50 rounded-xl">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                              i === 0 ? 'bg-yellow-500 text-white' :
                              i === 1 ? 'bg-gray-400 text-white' :
                              i === 2 ? 'bg-amber-600 text-white' :
                              'bg-muted text-muted-foreground'
                            }`}>
                              {i + 1}
                            </div>
                            <div>
                              <p className="font-medium text-sm flex items-center gap-2">
                                <Icon className="w-4 h-4" />
                                {build.builder_name}
                              </p>
                              <p className="text-xs text-muted-foreground">
                                Ordered {build.order_count} times • ${build.calculated_price?.toFixed(2)}
                              </p>
                            </div>
                          </div>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => promoteToChefPick(build)}
                            className="gap-1 border-amber-500 dark:border-amber-400 hover:bg-amber-100 dark:hover:bg-amber-900/30 text-amber-600 dark:text-amber-400"
                          >
                            <Star className="w-3 h-3" />
                            Promote to Chef's Pick
                          </Button>
                        </div>
                        
                        <div className="flex flex-wrap gap-1">
                          {Object.entries(build.selections || {}).flatMap(([cat, selItems]) =>
                            selItems.map((item, idx) => (
                              <span
                                key={`${cat}-${idx}`}
                                className="px-2 py-0.5 bg-card text-xs rounded-full border"
                              >
                                {item.name}
                              </span>
                            ))
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Award className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">
                    Popular builds will appear here once customers start ordering!
                  </p>
                  <p className="text-xs text-muted-foreground mt-2">
                    Track which combinations are most loved by your customers.
                  </p>
                </div>
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Edit Modal - Full CRUD for Sections & Options */}
      <Dialog open={showEditModal} onOpenChange={(open) => {
        setShowEditModal(open);
        if (!open) setExpandedSections({});
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background text-foreground">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="w-5 h-5 text-accent" />
              Edit: {editingItem?.name}
            </DialogTitle>
          </DialogHeader>
          
          {editingItem && (
            <div className="space-y-6 mt-4">
              {/* Basic Info */}
              <div className="bg-muted/50 dark:bg-muted/30 rounded-xl p-4 border border-border">
                <h4 className="font-medium mb-4 flex items-center gap-2 text-foreground">
                  <Settings className="w-4 h-4" />
                  Basic Information
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-foreground">Name *</Label>
                    <Input
                      value={editingItem.name}
                      onChange={(e) => setEditingItem(prev => ({ ...prev, name: e.target.value }))}
                      className="bg-background text-foreground border-border"
                      data-testid="edit-item-name"
                    />
                  </div>
                  <div>
                    <Label className="text-foreground">Base Price (FJD) *</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={editingItem.base_price}
                      onChange={(e) => setEditingItem(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                      className="bg-background text-foreground border-border"
                      data-testid="edit-item-price"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <Label className="text-foreground">Description</Label>
                  <Textarea
                    value={editingItem.description}
                    onChange={(e) => setEditingItem(prev => ({ ...prev, description: e.target.value }))}
                    rows={2}
                    className="bg-background text-foreground border-border"
                  />
                </div>
                
                <div className="mt-4">
                  <Label className="text-foreground">Item Image</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      value={editingItem.image}
                      onChange={(e) => setEditingItem(prev => ({ ...prev, image: e.target.value }))}
                      placeholder="https://... or upload an image"
                      className="bg-background text-foreground border-border flex-1"
                    />
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        onChange={(e) => handleImageUpload(e, true)}
                        disabled={uploadingImage}
                      />
                      <span className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors h-10 ${
                        uploadingImage 
                          ? 'bg-muted text-muted-foreground cursor-not-allowed' 
                          : 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20'
                      }`}>
                        {uploadingImage ? (
                          <RefreshCw className="w-4 h-4 animate-spin" />
                        ) : (
                          <Upload className="w-4 h-4" />
                        )}
                        {uploadingImage ? 'Uploading...' : 'Upload'}
                      </span>
                    </label>
                  </div>
                  {editingItem.image && (
                    <div className="mt-2 relative inline-block">
                      <img src={editingItem.image} alt="Preview" className="h-24 rounded-lg object-cover" />
                      <button
                        type="button"
                        onClick={() => setEditingItem(prev => ({ ...prev, image: '' }))}
                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </div>
                  )}
                </div>
                
                <div className="grid md:grid-cols-2 gap-4 mt-4">
                  <div>
                    <Label className="text-foreground">Prep Time (minutes)</Label>
                    <Input
                      type="number"
                      value={editingItem.prep_time_minutes}
                      onChange={(e) => setEditingItem(prev => ({ ...prev, prep_time_minutes: parseInt(e.target.value) || 10 }))}
                      className="bg-background text-foreground border-border"
                    />
                  </div>
                  <div className="flex items-center gap-3 pt-6">
                    <Switch
                      checked={editingItem.is_available}
                      onCheckedChange={(checked) => setEditingItem(prev => ({ ...prev, is_available: checked }))}
                      id="edit_is_available"
                    />
                    <Label htmlFor="edit_is_available" className="text-foreground">Available for ordering</Label>
                  </div>
                </div>
              </div>
              
              {/* Sections Manager */}
              <div className="border border-border rounded-xl overflow-hidden">
                <div className="bg-muted/50 dark:bg-muted/30 px-4 py-3 flex items-center justify-between border-b border-border">
                  <h4 className="font-medium flex items-center gap-2 text-foreground">
                    <Package className="w-4 h-4" />
                    Sections ({editingItem.sections?.length || 0})
                  </h4>
                  <Button
                    size="sm"
                    onClick={() => addSection(false)}
                    className="gap-1 bg-accent hover:bg-accent/90"
                    data-testid="edit-add-section-btn"
                  >
                    <Plus className="w-3 h-3" />
                    Add Section
                  </Button>
                </div>
                
                <div className="max-h-[400px] overflow-y-auto">
                  <AnimatePresence>
                    {editingItem.sections?.map((section, sIndex) => (
                      <motion.div
                        key={sIndex}
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        exit={{ opacity: 0, height: 0 }}
                        className="border-b border-border last:border-b-0"
                      >
                        {/* Section Header */}
                        <div 
                          className="px-4 py-3 bg-background hover:bg-muted/30 cursor-pointer flex items-center justify-between"
                          onClick={() => toggleSectionExpand(sIndex)}
                        >
                          <div className="flex items-center gap-3">
                            <GripVertical className="w-4 h-4 text-muted-foreground" />
                            <div>
                              <p className="font-medium text-sm text-foreground">{section.name || `Section ${sIndex + 1}`}</p>
                              <p className="text-xs text-muted-foreground">
                                {section.options?.length || 0} options • Min: {section.min_selections} • Max: {section.max_selections}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-destructive hover:text-destructive hover:bg-destructive/10 dark:text-red-400 dark:hover:bg-red-900/30"
                              onClick={(e) => {
                                e.stopPropagation();
                                removeSection(sIndex, false);
                              }}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                            {expandedSections[sIndex] ? (
                              <ChevronUp className="w-4 h-4 text-muted-foreground" />
                            ) : (
                              <ChevronDown className="w-4 h-4 text-muted-foreground" />
                            )}
                          </div>
                        </div>
                        
                        {/* Section Content (Expanded) */}
                        <AnimatePresence>
                          {expandedSections[sIndex] && (
                            <motion.div
                              initial={{ opacity: 0, height: 0 }}
                              animate={{ opacity: 1, height: 'auto' }}
                              exit={{ opacity: 0, height: 0 }}
                              className="px-4 pb-4 bg-muted/20 dark:bg-muted/10"
                            >
                              {/* Section Fields */}
                              <div className="grid md:grid-cols-2 gap-3 py-3 border-b border-border/50">
                                <div>
                                  <Label className="text-xs text-foreground">Section Name *</Label>
                                  <Input
                                    value={section.name}
                                    onChange={(e) => updateSection(sIndex, 'name', e.target.value, false)}
                                    placeholder="e.g., Choose Your Bun"
                                    className="h-8 text-sm bg-background text-foreground border-border"
                                    data-testid={`edit-section-${sIndex}-name`}
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-foreground">Category ID *</Label>
                                  <Input
                                    value={section.category}
                                    onChange={(e) => updateSection(sIndex, 'category', e.target.value, false)}
                                    placeholder="e.g., bread, protein, sauce"
                                    className="h-8 text-sm bg-background text-foreground border-border"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-foreground">Min Selections</Label>
                                  <Input
                                    type="number"
                                    min="0"
                                    value={section.min_selections}
                                    onChange={(e) => updateSection(sIndex, 'min_selections', parseInt(e.target.value) || 0, false)}
                                    className="h-8 text-sm bg-background text-foreground border-border"
                                  />
                                </div>
                                <div>
                                  <Label className="text-xs text-foreground">Max Selections</Label>
                                  <Input
                                    type="number"
                                    min="1"
                                    value={section.max_selections}
                                    onChange={(e) => updateSection(sIndex, 'max_selections', parseInt(e.target.value) || 1, false)}
                                    className="h-8 text-sm bg-background text-foreground border-border"
                                  />
                                </div>
                              </div>
                              
                              {/* Options */}
                              <div className="mt-3">
                                <div className="flex items-center justify-between mb-2">
                                  <Label className="text-xs font-medium text-foreground">Options</Label>
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="h-6 text-xs gap-1 border-border text-foreground hover:bg-muted"
                                    onClick={() => addOption(sIndex, false)}
                                    data-testid={`edit-section-${sIndex}-add-option`}
                                  >
                                    <Plus className="w-3 h-3" />
                                    Add Option
                                  </Button>
                                </div>
                                
                                <div className="space-y-2">
                                  {section.options?.map((option, oIndex) => (
                                    <div key={oIndex} className="flex items-center gap-2 p-2 bg-background rounded-lg border border-border">
                                      <Input
                                        value={option.name}
                                        onChange={(e) => updateOption(sIndex, oIndex, 'name', e.target.value, false)}
                                        placeholder="Option name"
                                        className="flex-1 h-7 text-xs bg-background text-foreground border-border"
                                        data-testid={`edit-option-${sIndex}-${oIndex}-name`}
                                      />
                                      <div className="flex items-center gap-1">
                                        <span className="text-xs text-muted-foreground">$</span>
                                        <Input
                                          type="number"
                                          step="0.01"
                                          value={option.price}
                                          onChange={(e) => updateOption(sIndex, oIndex, 'price', parseFloat(e.target.value) || 0, false)}
                                          className="w-16 h-7 text-xs bg-background text-foreground border-border"
                                        />
                                      </div>
                                      <div className="flex items-center gap-1">
                                        <input
                                          type="checkbox"
                                          checked={option.is_default}
                                          onChange={(e) => updateOption(sIndex, oIndex, 'is_default', e.target.checked, false)}
                                          className="w-3 h-3"
                                          title="Default option"
                                        />
                                        <span className="text-xs text-muted-foreground">Def</span>
                                      </div>
                                      <Button
                                        size="sm"
                                        variant="ghost"
                                        className="h-6 w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 dark:text-red-400 dark:hover:bg-red-900/30"
                                        onClick={() => removeOption(sIndex, oIndex, false)}
                                      >
                                        <X className="w-3 h-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            </motion.div>
                          )}
                        </AnimatePresence>
                      </motion.div>
                    ))}
                  </AnimatePresence>
                  
                  {(!editingItem.sections || editingItem.sections.length === 0) && (
                    <div className="p-8 text-center">
                      <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                      <p className="text-sm text-muted-foreground">No sections yet</p>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => addSection(false)}
                        className="mt-2 gap-1 border-border text-foreground hover:bg-muted"
                      >
                        <Plus className="w-3 h-3" />
                        Add First Section
                      </Button>
                    </div>
                  )}
                </div>
              </div>
              
              {/* Action Buttons */}
              <div className="flex gap-3 pt-4 border-t border-border">
                <Button 
                  variant="outline" 
                  onClick={() => setShowEditModal(false)} 
                  className="flex-1 border-border text-foreground hover:bg-muted"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveEditedItem}
                  disabled={saving}
                  className="flex-1 bg-accent hover:bg-accent/90"
                  data-testid="save-edit-btn"
                >
                  {saving ? (
                    <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Saving...</>
                  ) : (
                    <><Save className="w-4 h-4 mr-2" /> Save Changes</>
                  )}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Create New Item Modal */}
      <Dialog open={showCreateModal} onOpenChange={(open) => {
        setShowCreateModal(open);
        if (!open) {
          setNewItem({ ...EMPTY_ITEM });
          setExpandedSections({});
        }
      }}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-background text-foreground">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5 text-accent" />
              Create New Build Your Own Item
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 mt-4">
            {/* Basic Info */}
            <div className="bg-muted/50 dark:bg-muted/30 rounded-xl p-4 border border-border">
              <h4 className="font-medium mb-4 flex items-center gap-2 text-foreground">
                <Settings className="w-4 h-4" />
                Basic Information
              </h4>
              <div className="grid md:grid-cols-2 gap-4">
                <div>
                  <Label className="text-foreground">Name *</Label>
                  <Input
                    value={newItem.name}
                    onChange={(e) => setNewItem(prev => ({ ...prev, name: e.target.value }))}
                    placeholder="e.g., Build Your Own Salad"
                    className="bg-background text-foreground border-border"
                    data-testid="create-item-name"
                  />
                </div>
                <div>
                  <Label className="text-foreground">Base Price (FJD) *</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={newItem.base_price || ''}
                    onChange={(e) => setNewItem(prev => ({ ...prev, base_price: parseFloat(e.target.value) || 0 }))}
                    placeholder="0.00"
                    className="bg-background text-foreground border-border"
                    data-testid="create-item-price"
                  />
                </div>
              </div>
              
              <div className="mt-4">
                <Label className="text-foreground">Description</Label>
                <Textarea
                  value={newItem.description}
                  onChange={(e) => setNewItem(prev => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  placeholder="Describe what customers can build..."
                  className="bg-background text-foreground border-border"
                />
              </div>
              
              <div className="mt-4">
                <Label className="text-foreground">Item Image</Label>
                <div className="flex gap-2 mt-1">
                  <Input
                    value={newItem.image}
                    onChange={(e) => setNewItem(prev => ({ ...prev, image: e.target.value }))}
                    placeholder="https://... or upload an image"
                    className="bg-background text-foreground border-border flex-1"
                  />
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      accept="image/*"
                      className="hidden"
                      onChange={(e) => handleImageUpload(e, false)}
                      disabled={uploadingImage}
                    />
                    <span className={`inline-flex items-center gap-2 px-3 py-2 text-sm rounded-lg border transition-colors h-10 ${
                      uploadingImage 
                        ? 'bg-muted text-muted-foreground cursor-not-allowed' 
                        : 'bg-primary/10 text-primary border-primary/20 hover:bg-primary/20'
                    }`}>
                      {uploadingImage ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Upload className="w-4 h-4" />
                      )}
                      {uploadingImage ? 'Uploading...' : 'Upload'}
                    </span>
                  </label>
                </div>
                {newItem.image && (
                  <div className="mt-2 relative inline-block">
                    <img src={newItem.image} alt="Preview" className="h-24 rounded-lg object-cover" />
                    <button
                      type="button"
                      onClick={() => setNewItem(prev => ({ ...prev, image: '' }))}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="grid md:grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-foreground">Prep Time (minutes)</Label>
                  <Input
                    type="number"
                    value={newItem.prep_time_minutes}
                    onChange={(e) => setNewItem(prev => ({ ...prev, prep_time_minutes: parseInt(e.target.value) || 10 }))}
                    className="bg-background text-foreground border-border"
                  />
                </div>
                <div className="flex items-center gap-3 pt-6">
                  <Switch
                    checked={newItem.is_available}
                    onCheckedChange={(checked) => setNewItem(prev => ({ ...prev, is_available: checked }))}
                    id="create_is_available"
                  />
                  <Label htmlFor="create_is_available" className="text-foreground">Available for ordering</Label>
                </div>
              </div>
            </div>
            
            {/* Sections Manager */}
            <div className="border border-border rounded-xl overflow-hidden">
              <div className="bg-muted/50 dark:bg-muted/30 px-4 py-3 flex items-center justify-between border-b border-border">
                <h4 className="font-medium flex items-center gap-2 text-foreground">
                  <Package className="w-4 h-4" />
                  Sections ({newItem.sections?.length || 0})
                </h4>
                <Button
                  size="sm"
                  onClick={() => addSection(true)}
                  className="gap-1 bg-accent hover:bg-accent/90"
                  data-testid="create-add-section-btn"
                >
                  <Plus className="w-3 h-3" />
                  Add Section
                </Button>
              </div>
              
              <div className="max-h-[350px] overflow-y-auto">
                <AnimatePresence>
                  {newItem.sections?.map((section, sIndex) => (
                    <motion.div
                      key={sIndex}
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      className="border-b border-border last:border-b-0"
                    >
                      {/* Section Header */}
                      <div 
                        className="px-4 py-3 bg-background hover:bg-muted/30 cursor-pointer flex items-center justify-between"
                        onClick={() => toggleSectionExpand(sIndex)}
                      >
                        <div className="flex items-center gap-3">
                          <GripVertical className="w-4 h-4 text-muted-foreground" />
                          <div>
                            <p className="font-medium text-sm text-foreground">{section.name || `Section ${sIndex + 1}`}</p>
                            <p className="text-xs text-muted-foreground">
                              {section.options?.length || 0} options • Min: {section.min_selections} • Max: {section.max_selections}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            className="text-destructive hover:text-destructive hover:bg-destructive/10 dark:text-red-400 dark:hover:bg-red-900/30"
                            onClick={(e) => {
                              e.stopPropagation();
                              removeSection(sIndex, true);
                            }}
                          >
                            <Trash2 className="w-3 h-3" />
                          </Button>
                          {expandedSections[sIndex] ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          )}
                        </div>
                      </div>
                      
                      {/* Section Content (Expanded) */}
                      <AnimatePresence>
                        {expandedSections[sIndex] && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="px-4 pb-4 bg-muted/20 dark:bg-muted/10"
                          >
                            {/* Section Fields */}
                            <div className="grid md:grid-cols-2 gap-3 py-3 border-b border-border/50">
                              <div>
                                <Label className="text-xs text-foreground">Section Name *</Label>
                                <Input
                                  value={section.name}
                                  onChange={(e) => updateSection(sIndex, 'name', e.target.value, true)}
                                  placeholder="e.g., Choose Your Base"
                                  className="h-8 text-sm bg-background text-foreground border-border"
                                  data-testid={`create-section-${sIndex}-name`}
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-foreground">Category ID *</Label>
                                <Input
                                  value={section.category}
                                  onChange={(e) => updateSection(sIndex, 'category', e.target.value, true)}
                                  placeholder="e.g., base, topping, dressing"
                                  className="h-8 text-sm bg-background text-foreground border-border"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-foreground">Min Selections</Label>
                                <Input
                                  type="number"
                                  min="0"
                                  value={section.min_selections}
                                  onChange={(e) => updateSection(sIndex, 'min_selections', parseInt(e.target.value) || 0, true)}
                                  className="h-8 text-sm bg-background text-foreground border-border"
                                />
                              </div>
                              <div>
                                <Label className="text-xs text-foreground">Max Selections</Label>
                                <Input
                                  type="number"
                                  min="1"
                                  value={section.max_selections}
                                  onChange={(e) => updateSection(sIndex, 'max_selections', parseInt(e.target.value) || 1, true)}
                                  className="h-8 text-sm bg-background text-foreground border-border"
                                />
                              </div>
                            </div>
                            
                            {/* Options */}
                            <div className="mt-3">
                              <div className="flex items-center justify-between mb-2">
                                <Label className="text-xs font-medium text-foreground">Options</Label>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  className="h-6 text-xs gap-1 border-border text-foreground hover:bg-muted"
                                  onClick={() => addOption(sIndex, true)}
                                  data-testid={`create-section-${sIndex}-add-option`}
                                >
                                  <Plus className="w-3 h-3" />
                                  Add Option
                                </Button>
                              </div>
                              
                              <div className="space-y-2">
                                {section.options?.map((option, oIndex) => (
                                  <div key={oIndex} className="flex items-center gap-2 p-2 bg-background rounded-lg border border-border">
                                    <Input
                                      value={option.name}
                                      onChange={(e) => updateOption(sIndex, oIndex, 'name', e.target.value, true)}
                                      placeholder="Option name"
                                      className="flex-1 h-7 text-xs bg-background text-foreground border-border"
                                      data-testid={`create-option-${sIndex}-${oIndex}-name`}
                                    />
                                    <div className="flex items-center gap-1">
                                      <span className="text-xs text-muted-foreground">$</span>
                                      <Input
                                        type="number"
                                        step="0.01"
                                        value={option.price}
                                        onChange={(e) => updateOption(sIndex, oIndex, 'price', parseFloat(e.target.value) || 0, true)}
                                        className="w-16 h-7 text-xs bg-background text-foreground border-border"
                                      />
                                    </div>
                                    <div className="flex items-center gap-1">
                                      <input
                                        type="checkbox"
                                        checked={option.is_default}
                                        onChange={(e) => updateOption(sIndex, oIndex, 'is_default', e.target.checked, true)}
                                        className="w-3 h-3"
                                        title="Default option"
                                      />
                                      <span className="text-xs text-muted-foreground">Def</span>
                                    </div>
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      className="h-6 w-6 p-0 text-destructive hover:text-destructive hover:bg-destructive/10 dark:text-red-400 dark:hover:bg-red-900/30"
                                      onClick={() => removeOption(sIndex, oIndex, true)}
                                    >
                                      <X className="w-3 h-3" />
                                    </Button>
                                  </div>
                                ))}
                              </div>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </motion.div>
                  ))}
                </AnimatePresence>
                
                {(!newItem.sections || newItem.sections.length === 0) && (
                  <div className="p-8 text-center">
                    <Package className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">No sections yet</p>
                    <p className="text-xs text-muted-foreground mt-1">Add sections to define what customers can customize</p>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => addSection(true)}
                      className="mt-3 gap-1 border-border text-foreground hover:bg-muted"
                    >
                      <Plus className="w-3 h-3" />
                      Add First Section
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Action Buttons */}
            <div className="flex gap-3 pt-4 border-t border-border">
              <Button 
                variant="outline" 
                onClick={() => setShowCreateModal(false)} 
                className="flex-1 border-border text-foreground hover:bg-muted"
              >
                Cancel
              </Button>
              <Button
                onClick={createItem}
                disabled={saving || !newItem.name || !newItem.base_price || newItem.sections.length === 0}
                className="flex-1 bg-accent hover:bg-accent/90"
                data-testid="save-create-btn"
              >
                {saving ? (
                  <><RefreshCw className="w-4 h-4 mr-2 animate-spin" /> Creating...</>
                ) : (
                  <><Plus className="w-4 h-4 mr-2" /> Create Item</>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Chef's Recommendation Modal */}
      <Dialog open={showChefModal} onOpenChange={setShowChefModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Star className="w-5 h-5 text-amber-500" />
              Create Chef's Recommendation
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-4">
            <div>
              <Label>Select Build-Your-Own Item *</Label>
              <Select
                value={newChefRec.builder_id}
                onValueChange={(value) => setNewChefRec(prev => ({ ...prev, builder_id: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Choose an item" />
                </SelectTrigger>
                <SelectContent className="z-[200]">
                  {items.filter(i => i.is_available !== false).map(item => (
                    <SelectItem key={item.id} value={item.id}>
                      {item.name} (${item.base_price?.toFixed(2)})
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Recommendation Name *</Label>
              <Input
                value={newChefRec.name}
                onChange={(e) => setNewChefRec(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g., Summer Sunrise Burger"
              />
            </div>

            <div>
              <Label>Description (optional)</Label>
              <Textarea
                value={newChefRec.description}
                onChange={(e) => setNewChefRec(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Why is this special? What makes it unique?"
                rows={2}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Badge Type</Label>
                <Select
                  value={newChefRec.badge_type}
                  onValueChange={(value) => setNewChefRec(prev => ({ ...prev, badge_type: value }))}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-[200]">
                    <SelectItem value="chef_pick">
                      <span className="flex items-center gap-2">
                        <Star className="w-3 h-3 text-amber-500" /> Chef's Pick
                      </span>
                    </SelectItem>
                    <SelectItem value="seasonal">
                      <span className="flex items-center gap-2">
                        <Leaf className="w-3 h-3 text-green-500" /> Seasonal
                      </span>
                    </SelectItem>
                    <SelectItem value="new">
                      <span className="flex items-center gap-2">
                        <Zap className="w-3 h-3 text-purple-500" /> New
                      </span>
                    </SelectItem>
                    <SelectItem value="staff_favorite">
                      <span className="flex items-center gap-2">
                        <Sparkles className="w-3 h-3 text-blue-500" /> Staff Favorite
                      </span>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Special Price (optional)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={newChefRec.special_price}
                  onChange={(e) => setNewChefRec(prev => ({ ...prev, special_price: e.target.value }))}
                  placeholder="Leave empty for regular price"
                />
              </div>
            </div>

            <p className="text-xs text-muted-foreground">
              Note: This will create a recommendation with default selections. You can edit the specific ingredients after creation.
            </p>

            <div className="flex gap-3 pt-4">
              <Button variant="outline" onClick={() => setShowChefModal(false)} className="flex-1">
                Cancel
              </Button>
              <Button
                onClick={createChefRecommendation}
                className="flex-1 bg-amber-500 hover:bg-amber-600"
              >
                <Star className="w-4 h-4 mr-2" />
                Create Recommendation
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
